import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionPlan } from '../action-plan/action-plan';
import { VerifyActionPlan } from '../verify-action-plan/verify-action-plan';
import { ProductSuggestion } from '../product-suggestion/product-suggestion';
import { MakeDecision } from '../make-decision/make-decision';
import { CheckDecision } from '../check-decision/check-decision';
import { TaskService } from '../../_services/task.service';
import { MatStepperModule } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatIconModule } from '@angular/material/icon';
import { ProductSuggestionResponse, Suggestion } from '../../_models/product-suggestion-res.model';
import {
  TranscriberContent,
  TranscriberSelection,
} from '../transcriber-content/transcriber-content';
import { DocumentSummary } from '../document-summary/document-summary';
import { Chat } from '../chat/chat';
import { combineLatest, Observable } from 'rxjs';
import { WorkProcessWithClient } from '../../_models/social-domain/workprocess-with-client.model';
import { WorkflowService } from '../../_services/workflow.service';
import { DocumentService } from '../../_services/document.service';
import { DocumentTypeEnum } from '../../_enums/document-type.enum';
import { SocialDomainService } from '../../_services/social-domain.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CustomSnackbar } from '../custom-snackbar/custom-snackbar';
import { SnackbarData } from '../../_models/snackbar-data.model';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { buildDocumentFilename } from '../../_helpers/document-filename.util';
import { StepBar } from '../step-bar/step-bar';

@Component({
  selector: 'app-workflow',
  imports: [
    TranslatePipe,
    ActionPlan,
    VerifyActionPlan,
    ProductSuggestion,
    MakeDecision,
    CheckDecision,
    DocumentSummary,
    Chat,
    MatStepperModule,
    MatIconModule,
    TranscriberContent,
    StepBar,
  ],
  templateUrl: './workflow.html',
  styleUrl: './workflow.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Workflow implements OnInit {
  public transcriptFile: File | undefined;
  public actionPlanFile: File | undefined;

  public actionPlanVerdict: string | undefined;
  public actionPlanVerdictError: boolean = false;
  public isUploadInProgress = false;
  public uploadDone = false;
  public isSummaryUploadInProgress = false;
  public summaryUploadDone = false;

  public productSuggestionResponse: ProductSuggestionResponse | null = null;
  public productSuggestionError: boolean = false;
  public selectedProducts: Suggestion[] = [];

  public decisionFile: File | undefined;
  public isVerifyInProgress = false;
  public isProductSuggestionInProgress = false;

  public checkedDecisionOutput: string | undefined;
  public checkDecisionError = false;
  public isCheckDecisionInProgress = false;
  public isDecisionUploadInProgress = false;
  public decisionUploadDone = false;

  public selectedWorkProcess: WorkProcessWithClient | null = null;
  public selectedIndex = 0;
  // Tijdens het herstarten is de stapinhoud even weg, zodat de componenten (en dus de
  // transcriber-widget) opnieuw worden aangemaakt in plaats van hergebruikt.
  public isResettingStep = false;

  public stepLabels = [
    'stepper.transcribeAndSummarize',
    'stepper.createActionPlan',
    'stepper.verifyActionPlan',
    'stepper.productSuggestion',
    'stepper.createDecision',
    'stepper.checkDecision.title',
    'stepper.caseSummary',
    'stepper.chat',
  ];

  public stepsCompleted: boolean[] = Array(this.stepLabels.length).fill(false);
  private readonly taskService = inject(TaskService);
  private readonly workflowService = inject(WorkflowService);
  private readonly documentService = inject(DocumentService);
  private readonly socialDomainService = inject(SocialDomainService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);

  private showSnackbar(translationKey: string, type: SnackbarType): void {
    this.translateService.get(translationKey).subscribe((message: string) => {
      this.snackBar.openFromComponent(CustomSnackbar, {
        duration: 5000,
        data: new SnackbarData(message, type),
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      });
    });
  }

  public ngOnInit(): void {
    this.subscribeToWorkflowReset();
    this.subscribeToDocumentChanges();
  }

  public onStepHeaderChange(event: StepperSelectionEvent): void {
    this.selectStep(event.selectedIndex);
  }

  public selectStep(index: number): void {
    this.selectedIndex = index;
    // Optionele documenten horen bij één stap; bij het wisselen van stap vervallen ze.
    this.documentService.removeAllOptionalDocuments();
  }

  public onTranscriptSummarized(): void {
    this.stepsCompleted[0] = true;
  }

  public onSummaryGenerated(): void {
    this.stepsCompleted[6] = true;
  }

  /**
   * Een stap is afgerond zodra het resultaat van die stap gegenereerd is — dat gebeurt
   * terwijl de gebruiker op de stap staat, dus niet pas bij het doorklikken. Verdwijnt
   * het resultaat weer (rerun, document verwijderd), dan vervalt ook het vinkje.
   *
   * Documentstappen (transcript, actieplan, beschikking) vereisen daarnaast de
   * stepsCompleted-vlag: hun document kan ook vanaf een andere stap ontstaan
   * (transcript bestaat al halverwege stap 1, beschikking is te uploaden bij
   * "beschikking controleren") en dat mag de stap zelf niet afvinken.
   */
  public isStepCompleted(index: number): boolean {
    switch (index) {
      case 0:
        return this.stepsCompleted[index] && !!this.transcriptFile;
      case 1:
        return this.stepsCompleted[index] && !!this.actionPlanFile;
      case 2:
        return !!this.actionPlanVerdict;
      case 3:
        return !!this.productSuggestionResponse;
      case 4:
        return this.stepsCompleted[index] && !!this.decisionFile;
      case 5:
        return !!this.checkedDecisionOutput;
      default:
        // Dossiersamenvatting en chat hebben geen afleidbaar resultaat in dit component.
        return this.stepsCompleted[index];
    }
  }

  public completedSteps(): boolean[] {
    return this.stepLabels.map((_, index) => this.isStepCompleted(index));
  }

  public onTranscriberSelectionChanged(selection: TranscriberSelection): void {
    if (selection.workProcess !== this.selectedWorkProcess) {
      this.onWorkProcessSelected(selection.workProcess);
    }
  }

  public rerunVerifyActionPlan(): void {
    if (!this.requireActionPlanFile()) return;

    this.actionPlanVerdict = undefined;
    this.actionPlanVerdictError = false;
    this.isUploadInProgress = false;
    this.runVerifyActionPlan();
  }

  public onWorkProcessSelected(workProcess: WorkProcessWithClient | null): void {
    this.selectedWorkProcess = workProcess;
    // Ander werkproces = daar is nog niets geüpload.
    this.uploadDone = false;
    this.decisionUploadDone = false;
    this.summaryUploadDone = false;
  }

  public uploadActionPlanToSsd(): void {
    if (!this.selectedWorkProcess || !this.actionPlanFile) return;

    const filename = this.buildSsdFilename(DocumentTypeEnum.ACTION_PLAN, 'actieplan');
    this.isUploadInProgress = true;
    this.socialDomainService
      .uploadDocument(this.selectedWorkProcess.werkprocesNummer, this.actionPlanFile, filename)
      .subscribe({
        next: () => {
          this.isUploadInProgress = false;
          this.uploadDone = true;
          this.showSnackbar('upload.uploadToSsdSuccess', SnackbarType.SUCCESS);
        },
        error: (error) => {
          this.isUploadInProgress = false;
          this.showUploadErrorSnackbar(error);
        },
      });
  }

  public uploadSummaryToSsd(summaryFile: File): void {
    if (!this.selectedWorkProcess) return;

    this.isSummaryUploadInProgress = true;
    this.socialDomainService
      .uploadDocument(this.selectedWorkProcess.werkprocesNummer, summaryFile, summaryFile.name)
      .subscribe({
        next: () => {
          this.isSummaryUploadInProgress = false;
          this.summaryUploadDone = true;
          this.showSnackbar('upload.uploadSummaryToSsdSuccess', SnackbarType.SUCCESS);
        },
        error: (error) => {
          this.isSummaryUploadInProgress = false;
          this.showUploadErrorSnackbar(error);
        },
      });
  }

  public uploadDecisionToSsd(): void {
    if (!this.selectedWorkProcess || !this.decisionFile) return;

    const filename = this.buildSsdFilename(DocumentTypeEnum.DECISION, 'beschikking');
    this.isDecisionUploadInProgress = true;
    this.socialDomainService
      .uploadDocument(this.selectedWorkProcess.werkprocesNummer, this.decisionFile, filename)
      .subscribe({
        next: () => {
          this.isDecisionUploadInProgress = false;
          this.decisionUploadDone = true;
          this.showSnackbar('upload.uploadDecisionToSsdSuccess', SnackbarType.SUCCESS);
        },
        error: (error) => {
          this.isDecisionUploadInProgress = false;
          this.showUploadErrorSnackbar(error);
        },
      });
  }

  // Named after the template the document was made from, e.g.
  // ondersteuningsplan-jw-2283-2026-10-09.docx; a document the user uploaded keeps
  // the generic kind (actieplan, beschikking).
  private buildSsdFilename(type: DocumentTypeEnum, kind: string): string {
    return buildDocumentFilename(
      this.documentService.getDocumentTitle(type) ?? kind,
      this.selectedWorkProcess?.werkprocesNummer,
    );
  }

  private showUploadErrorSnackbar(error: { status?: number } | null | undefined): void {
    if (error?.status === 422) {
      // UploadRejectedException: geen documentinrichting voor deze werkprocessoort
      this.showSnackbar('upload.uploadToSsdRejected', SnackbarType.WARNING);
    } else {
      this.showSnackbar('upload.uploadToSsdError', SnackbarType.ERROR);
    }
  }

  public rerunProductSuggestion(): void {
    this.productSuggestionResponse = null;
    this.productSuggestionError = false;
    this.selectedProducts = [];
    this.runProductSuggestion();
  }

  public rerunCheckDecision(): void {
    this.checkedDecisionOutput = undefined;
    this.checkDecisionError = false;
    // Bewust géén reset van decisionUploadDone — zelfde reden als bij het
    // actieplan: één upload per workflow, ook na een nieuwe controle.

    this.runCheckDecision();
  }

  private runVerifyActionPlan(): void {
    const optionalFiles = this.documentService.getOptionalDocumentsValue();
    this.isVerifyInProgress = true;
    this.executeRequest(
      this.taskService.verifyActionPlan(this.actionPlanFile!, optionalFiles),
      (response) => {
        this.actionPlanVerdict = response.reden;
        this.actionPlanVerdictError = false;
        this.isVerifyInProgress = false;
      },
      () => {
        this.actionPlanVerdictError = true;
        this.isVerifyInProgress = false;
      },
    );
  }

  private subscribeToWorkflowReset(): void {
    this.workflowService.getReset().subscribe(() => {
      this.resetWorkflow();
    });
  }

  private subscribeToDocumentChanges(): void {
    combineLatest([
      this.documentService.getDocument(DocumentTypeEnum.TRANSCRIPT),
      this.documentService.getDocument(DocumentTypeEnum.ACTION_PLAN),
      this.documentService.getDocument(DocumentTypeEnum.DECISION),
    ]).subscribe(([transcriptFile, actionPlanFile, decisionFile]) => {
      this.transcriptFile = transcriptFile;
      this.actionPlanFile = actionPlanFile;
      this.decisionFile = decisionFile;
      this.markDocumentStepIfSelected(1, actionPlanFile);
      this.markDocumentStepIfSelected(4, decisionFile);
    });
  }

  // Alleen een document dat binnenkomt terwijl de eigen stap geselecteerd is
  // (gegenereerd of daar geüpload), telt als afronding van die stap.
  private markDocumentStepIfSelected(stepIndex: number, file: File | undefined): void {
    if (file && this.selectedIndex === stepIndex) {
      this.stepsCompleted[stepIndex] = true;
    }
  }

  private runProductSuggestion(): void {
    if (!this.actionPlanFile) {
      return;
    }

    const optionalFiles = this.documentService.getOptionalDocumentsValue();

    this.isProductSuggestionInProgress = true;
    this.executeRequest(
      this.taskService.productSuggestion({
        actionPlan: this.actionPlanFile,
        products: ['Product 1', 'Product 2', 'Product 3'],
        documents: optionalFiles,
      }),
      (response) => {
        this.productSuggestionResponse = response;
        this.productSuggestionError = false;
        this.isProductSuggestionInProgress = false;
      },
      () => {
        this.productSuggestionError = true;
        this.isProductSuggestionInProgress = false;
      },
    );
  }

  private runCheckDecision(): void {
    if (!this.decisionFile) {
      return;
    }

    const optionalFiles = this.documentService.getOptionalDocumentsValue();

    this.isCheckDecisionInProgress = true;
    this.executeRequest(
      this.taskService.checkDecision(this.decisionFile, optionalFiles),
      (response) => {
        this.checkedDecisionOutput = response.reason;
        this.checkDecisionError = false;
        this.isCheckDecisionInProgress = false;
      },
      () => {
        this.checkDecisionError = true;
        this.isCheckDecisionInProgress = false;
      },
    );
  }

  private executeRequest<T>(
    request$: Observable<T>,
    onSuccess: (value: T) => void,
    onError: () => void,
  ): void {
    request$.subscribe({
      next: onSuccess,
      error: onError,
    });
  }

  private requireActionPlanFile(): boolean {
    return !!this.actionPlanFile;
  }

  /**
   * Alles terug naar de begintoestand. De route-hop die hier stond ('/' en dan weer
   * '/transcriber') deed niets: er is geen route op '/', dus die navigatie strandde op de
   * wildcard — en zelfs met een route erbij zou Angular een navigatie naar dezelfde URL
   * negeren. Daarom zetten we de eigen velden terug en bouwen we de stapinhoud opnieuw op.
   */
  private resetWorkflow(): void {
    this.workflowService.setStepInProgress(false);

    // De documentservice leeft buiten deze component; zonder dit zou het transcript van
    // het vorige gesprek in de vervolgstappen blijven staan.
    Object.values(DocumentTypeEnum).forEach((type) => {
      this.documentService.deleteDocument(type);
      this.documentService.setDocumentChanged(type, false);
    });
    this.documentService.clearOptionalDocuments();

    this.isUploadInProgress = false;
    this.uploadDone = false;
    this.isDecisionUploadInProgress = false;
    this.decisionUploadDone = false;
    this.isSummaryUploadInProgress = false;
    this.summaryUploadDone = false;

    this.actionPlanVerdict = undefined;
    this.actionPlanVerdictError = false;
    this.isVerifyInProgress = false;
    this.productSuggestionResponse = null;
    this.productSuggestionError = false;
    this.isProductSuggestionInProgress = false;
    this.selectedProducts = [];
    this.checkedDecisionOutput = undefined;
    this.checkDecisionError = false;
    this.isCheckDecisionInProgress = false;

    this.selectedWorkProcess = null;
    this.stepsCompleted = Array(this.stepLabels.length).fill(false);
    this.selectedIndex = 0;

    // De transcriber-widget houdt zijn eigen opname en transcript vast; alleen opnieuw
    // aanmaken maakt die echt leeg.
    this.isResettingStep = true;
    setTimeout(() => (this.isResettingStep = false));
  }
}
